import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Grid,
  Paper,
  Button,
  AppBar,
  Toolbar,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  TextField,
  MenuItem,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckIcon from '@mui/icons-material/Check';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import api from '../services/api';

const SITE_TOKEN_KEY = 'site_token';
const LEGACY_TOKEN_KEY = 'token';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [appointments, setAppointments] = useState([]);
  const [blogs, setBlogs] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [cases, setCases] = useState([]);
  const [practiceAreas, setPracticeAreas] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [newTestimonial, setNewTestimonial] = useState({
    clientName: '',
    clientEmail: '',
    rating: 5,
    caseType: '',
    content: '',
  });
  const [newPracticeArea, setNewPracticeArea] = useState({
    title: '',
    description: '',
    services: '',
    order: 1,
  });
  const [newBlog, setNewBlog] = useState({
    title: '',
    category: '',
    excerpt: '',
    content: '',
    tags: '',
  });
  const [newCaseStudy, setNewCaseStudy] = useState({
    title: '',
    category: '',
    description: '',
    challenge: '',
    solution: '',
    result: '',
  });

  useEffect(() => {
    const init = async () => {
      try {
        const token = localStorage.getItem(SITE_TOKEN_KEY) || localStorage.getItem(LEGACY_TOKEN_KEY);
        if (!token) {
          navigate('/admin/login');
          return;
        }
        localStorage.setItem(SITE_TOKEN_KEY, token);
        const me = await api.get('/auth/me');
        if (me.data.role !== 'admin') {
          navigate('/admin/login');
          return;
        }

        const [apptsRes, blogsRes, testiRes, casesRes, areasRes, contactsRes] = await Promise.all([
          api.get('/appointments'),
          api.get('/blogs'),
          api.get('/testimonials/all'),
          api.get('/cases'),
          api.get('/practice-areas'),
          api.get('/contact'),
        ]);

        setAppointments(apptsRes.data.appointments || []);
        setBlogs(blogsRes.data.blogs || []);
        setTestimonials(testiRes.data || []);
        setCases(casesRes.data || []);
        setPracticeAreas(areasRes.data || []);
        setContacts(contactsRes.data || []);
      } catch (error) {
        console.error(error);
        navigate('/admin/login');
      } finally {
        setLoading(false);
      }
    };

    init();
  }, [navigate]);

  const handleLogout = () => {
    localStorage.removeItem(SITE_TOKEN_KEY);
    navigate('/admin/login');
  };

  const updateAppointmentStatus = async (id, status) => {
    try {
      await api.put(`/appointments/${id}`, { status });
      setAppointments((prev) =>
        prev.map((a) => (a._id === id ? { ...a, status } : a)),
      );
    } catch (error) {
      console.error(error);
    }
  };

  const deleteAppointment = async (id) => {
    try {
      await api.delete(`/appointments/${id}`);
      setAppointments((prev) => prev.filter((a) => a._id !== id));
    } catch (error) {
      console.error(error);
    }
  };

  const toggleTestimonialApproval = async (id, approved) => {
    try {
      await api.put(`/testimonials/${id}`, { approved });
      setTestimonials((prev) =>
        prev.map((t) => (t._id === id ? { ...t, approved } : t)),
      );
    } catch (error) {
      console.error(error);
    }
  };

  const deleteTestimonial = async (id) => {
    try {
      await api.delete(`/testimonials/${id}`);
      setTestimonials((prev) => prev.filter((t) => t._id !== id));
    } catch (error) {
      console.error(error);
    }
  };

  const deletePracticeArea = async (id) => {
    try {
      await api.delete(`/practice-areas/${id}`);
      setPracticeAreas((prev) => prev.filter((p) => p._id !== id));
    } catch (error) {
      console.error(error);
    }
  };

  const deleteBlog = async (id) => {
    try {
      await api.delete(`/blogs/${id}`);
      setBlogs((prev) => prev.filter((b) => b._id !== id));
    } catch (error) {
      console.error(error);
    }
  };

  const deleteCase = async (id) => {
    try {
      await api.delete(`/cases/${id}`);
      setCases((prev) => prev.filter((c) => c._id !== id));
    } catch (error) {
      console.error(error);
    }
  };

  const updateContactStatus = async (id, status) => {
    try {
      await api.put(`/contact/${id}`, { status });
      setContacts((prev) =>
        prev.map((c) => (c._id === id ? { ...c, status } : c)),
      );
    } catch (error) {
      console.error(error);
    }
  };

  const deleteContact = async (id) => {
    try {
      await api.delete(`/contact/${id}`);
      setContacts((prev) => prev.filter((c) => c._id !== id));
    } catch (error) {
      console.error(error);
    }
  };

  const stats = {
    appointments: appointments.length,
    blogs: blogs.length,
    testimonials: testimonials.length,
    cases: cases.length,
    contacts: contacts.length,
  };

  return (
    <Box>
      <AppBar position="static">
        <Toolbar>
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            Admin Dashboard
          </Typography>
          <Button color="inherit" onClick={handleLogout}>
            Logout
          </Button>
        </Toolbar>
      </AppBar>
      <Container sx={{ py: 4 }}>
        <Typography variant="h4" gutterBottom>
          Overview
        </Typography>
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Paper sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="h4">{stats.appointments}</Typography>
              <Typography variant="body2" color="text.secondary">
                Appointments
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Paper sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="h4">{stats.blogs}</Typography>
              <Typography variant="body2" color="text.secondary">
                Blog Posts
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Paper sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="h4">{stats.testimonials}</Typography>
              <Typography variant="body2" color="text.secondary">
                Testimonials
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Paper sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="h4">{stats.cases}</Typography>
              <Typography variant="body2" color="text.secondary">
                Case Studies
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Paper sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="h4">{stats.contacts}</Typography>
              <Typography variant="body2" color="text.secondary">
                Contact Messages
              </Typography>
            </Paper>
          </Grid>
        </Grid>

        <Paper sx={{ mb: 4 }}>
          <Tabs
            value={activeTab}
            onChange={(_, v) => setActiveTab(v)}
            variant="scrollable"
            scrollButtons="auto"
          >
            <Tab label="Appointments" />
            <Tab label="Contacts" />
            <Tab label="Testimonials" />
            <Tab label="Practice Areas" />
            <Tab label="Blogs" />
            <Tab label="Case Studies" />
          </Tabs>
        </Paper>

        {loading ? (
          <Typography>Loading...</Typography>
        ) : (
          <Box>
            {activeTab === 0 && (
              <Paper sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Appointments
                </Typography>
                <Box sx={{ width: '100%', overflowX: 'auto' }}>
                  <Table size="small" sx={{ minWidth: 650 }}>
                    <TableHead>
                      <TableRow>
                        <TableCell>Name</TableCell>
                        <TableCell>Date</TableCell>
                        <TableCell>Time</TableCell>
                        <TableCell>Service</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {appointments.map((a) => (
                        <TableRow key={a._id}>
                          <TableCell>{a.name}</TableCell>
                          <TableCell>
                            {a.date ? new Date(a.date).toLocaleDateString() : '-'}
                          </TableCell>
                          <TableCell>{a.time}</TableCell>
                          <TableCell>{a.service}</TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              label={a.status}
                              color={
                                a.status === 'confirmed'
                                  ? 'primary'
                                  : a.status === 'completed'
                                  ? 'success'
                                  : a.status === 'cancelled'
                                  ? 'error'
                                  : 'default'
                              }
                            />
                          </TableCell>
                          <TableCell align="right">
                            <IconButton
                              size="small"
                              title="Mark Confirmed"
                              onClick={() => updateAppointmentStatus(a._id, 'confirmed')}
                            >
                              <CheckIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              title="Mark Completed"
                              onClick={() => updateAppointmentStatus(a._id, 'completed')}
                            >
                              <DoneAllIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              color="error"
                              title="Delete"
                              onClick={() => deleteAppointment(a._id)}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
              </Paper>
            )}

            {activeTab === 1 && (
              <Paper sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Contact Messages
                </Typography>
                <Box sx={{ width: '100%', overflowX: 'auto' }}>
                  <Table size="small" sx={{ minWidth: 800 }}>
                    <TableHead>
                      <TableRow>
                        <TableCell>Name</TableCell>
                        <TableCell>Email</TableCell>
                        <TableCell>Phone</TableCell>
                        <TableCell>Subject</TableCell>
                        <TableCell>Message</TableCell>
                        <TableCell>Date</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {contacts.map((contact) => (
                        <TableRow key={contact._id}>
                          <TableCell>{contact.name}</TableCell>
                          <TableCell>{contact.email}</TableCell>
                          <TableCell>{contact.phone || '-'}</TableCell>
                          <TableCell>{contact.subject || '-'}</TableCell>
                          <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {contact.message}
                          </TableCell>
                          <TableCell>
                            {contact.createdAt ? new Date(contact.createdAt).toLocaleDateString() : '-'}
                          </TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              label={contact.status}
                              color={
                                contact.status === 'new'
                                  ? 'error'
                                  : contact.status === 'read'
                                  ? 'warning'
                                  : contact.status === 'replied'
                                  ? 'success'
                                  : 'default'
                              }
                            />
                          </TableCell>
                          <TableCell align="right">
                            <IconButton
                              size="small"
                              title="Mark as Read"
                              onClick={() => updateContactStatus(contact._id, 'read')}
                              disabled={contact.status === 'read' || contact.status === 'replied'}
                            >
                              <CheckIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              title="Mark as Replied"
                              onClick={() => updateContactStatus(contact._id, 'replied')}
                              disabled={contact.status === 'replied'}
                            >
                              <DoneAllIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              color="error"
                              title="Delete"
                              onClick={() => deleteContact(contact._id)}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
              </Paper>
            )}

            {activeTab === 2 && (
              <Paper sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Testimonials
                </Typography>
                <Box
                  component="form"
                  sx={{ mb: 3, display: 'flex', flexWrap: 'wrap', gap: 2 }}
                  onSubmit={async (e) => {
                    e.preventDefault();
                    try {
                      const res = await api.post('/testimonials', {
                        clientName: newTestimonial.clientName,
                        clientEmail: newTestimonial.clientEmail,
                        caseType: newTestimonial.caseType,
                        content: newTestimonial.content,
                        rating: newTestimonial.rating,
                      });
                      setTestimonials((prev) => [...prev, res.data]);
                      setNewTestimonial({
                        clientName: '',
                        clientEmail: '',
                        rating: 5,
                        caseType: '',
                        content: '',
                      });
                    } catch (err) {
                      console.error(err);
                    }
                  }}
                >
                  <Typography variant="subtitle1" sx={{ width: '100%' }}>
                    Add Testimonial
                  </Typography>
                  <TextField
                    label="Client Name"
                    size="small"
                    value={newTestimonial.clientName}
                    onChange={(e) =>
                      setNewTestimonial((p) => ({ ...p, clientName: e.target.value }))
                    }
                    required
                  />
                  <TextField
                    label="Client Email"
                    size="small"
                    value={newTestimonial.clientEmail}
                    onChange={(e) =>
                      setNewTestimonial((p) => ({ ...p, clientEmail: e.target.value }))
                    }
                  />
                  <TextField
                    label="Case Type"
                    size="small"
                    value={newTestimonial.caseType}
                    onChange={(e) =>
                      setNewTestimonial((p) => ({ ...p, caseType: e.target.value }))
                    }
                  />
                  <TextField
                    label="Rating (1–5)"
                    type="number"
                    size="small"
                    sx={{ width: 110 }}
                    inputProps={{ min: 1, max: 5 }}
                    value={newTestimonial.rating}
                    onChange={(e) =>
                      setNewTestimonial((p) => ({
                        ...p,
                        rating: Number(e.target.value),
                      }))
                    }
                    required
                  />
                  <TextField
                    label="Testimonial"
                    size="small"
                    sx={{ width: '100%' }}
                    multiline
                    minRows={2}
                    value={newTestimonial.content}
                    onChange={(e) =>
                      setNewTestimonial((p) => ({ ...p, content: e.target.value }))
                    }
                    required
                  />
                  <Box sx={{ width: '100%', textAlign: 'right' }}>
                    <Button type="submit" variant="contained" size="small">
                      Add
                    </Button>
                  </Box>
                </Box>
                <Box sx={{ width: '100%', overflowX: 'auto', mt: 2 }}>
                  <Table size="small" sx={{ minWidth: 600 }}>
                    <TableHead>
                      <TableRow>
                        <TableCell>Client</TableCell>
                        <TableCell>Rating</TableCell>
                        <TableCell>Case Type</TableCell>
                        <TableCell>Approved</TableCell>
                        <TableCell align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {testimonials.map((t) => (
                        <TableRow key={t._id}>
                          <TableCell>{t.clientName}</TableCell>
                          <TableCell>{t.rating}</TableCell>
                          <TableCell>{t.caseType || '-'}</TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              label={t.approved ? 'Approved' : 'Pending'}
                              color={t.approved ? 'success' : 'default'}
                            />
                          </TableCell>
                          <TableCell align="right">
                            <Button
                              size="small"
                              onClick={() => toggleTestimonialApproval(t._id, !t.approved)}
                            >
                              {t.approved ? 'Unapprove' : 'Approve'}
                            </Button>
                            <IconButton
                              size="small"
                              color="error"
                              title="Delete"
                              onClick={() => deleteTestimonial(t._id)}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
              </Paper>
            )}

            {activeTab === 3 && (
              <Paper sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Practice Areas
                </Typography>
                <Box
                  component="form"
                  sx={{ mb: 3, display: 'flex', flexWrap: 'wrap', gap: 2 }}
                  onSubmit={async (e) => {
                    e.preventDefault();
                    try {
                      const res = await api.post('/practice-areas', {
                        title: newPracticeArea.title,
                        description: newPracticeArea.description,
                        services: newPracticeArea.services,
                        order: newPracticeArea.order,
                        featured: true,
                      });
                      setPracticeAreas((prev) => [...prev, res.data]);
                      setNewPracticeArea({
                        title: '',
                        description: '',
                        services: '',
                        order: 1,
                      });
                    } catch (err) {
                      console.error(err);
                    }
                  }}
                >
                  <Typography variant="subtitle1" sx={{ width: '100%' }}>
                    Add Practice Area
                  </Typography>
                  <TextField
                    label="Title"
                    size="small"
                    value={newPracticeArea.title}
                    onChange={(e) =>
                      setNewPracticeArea((p) => ({ ...p, title: e.target.value }))
                    }
                    required
                  />
                  <TextField
                    label="Short Description"
                    size="small"
                    sx={{ flex: 1, minWidth: 220 }}
                    value={newPracticeArea.description}
                    onChange={(e) =>
                      setNewPracticeArea((p) => ({ ...p, description: e.target.value }))
                    }
                    required
                  />
                  <TextField
                    label="Services (comma separated)"
                    size="small"
                    sx={{ flex: 1, minWidth: 220 }}
                    value={newPracticeArea.services}
                    onChange={(e) =>
                      setNewPracticeArea((p) => ({ ...p, services: e.target.value }))
                    }
                  />
                  <TextField
                    label="Order"
                    size="small"
                    type="number"
                    sx={{ width: 100 }}
                    value={newPracticeArea.order}
                    onChange={(e) =>
                      setNewPracticeArea((p) => ({ ...p, order: Number(e.target.value) }))
                    }
                  />
                  <Button type="submit" variant="contained" size="small">
                    Add
                  </Button>
                </Box>
                <Box sx={{ width: '100%', overflowX: 'auto', mt: 2 }}>
                  <Table size="small" sx={{ minWidth: 600 }}>
                    <TableHead>
                      <TableRow>
                        <TableCell>Title</TableCell>
                        <TableCell>Order</TableCell>
                        <TableCell>Featured</TableCell>
                        <TableCell align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {practiceAreas.map((p) => (
                        <TableRow key={p._id}>
                          <TableCell>{p.title}</TableCell>
                          <TableCell>{p.order}</TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              label={p.featured ? 'Yes' : 'No'}
                              color={p.featured ? 'primary' : 'default'}
                            />
                          </TableCell>
                          <TableCell align="right">
                            <IconButton
                              size="small"
                              color="error"
                              title="Delete"
                              onClick={() => deletePracticeArea(p._id)}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
              </Paper>
            )}

            {activeTab === 4 && (
              <Paper sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Blogs
                </Typography>
                <Box
                  component="form"
                  sx={{ mb: 3, display: 'flex', flexWrap: 'wrap', gap: 2 }}
                  onSubmit={async (e) => {
                    e.preventDefault();
                    try {
                      const formData = new FormData();
                      formData.append('title', newBlog.title);
                      const allowedCategories = [
                        'Legal Updates',
                        'Case Studies',
                        'Legal Tips',
                        'News',
                        'General',
                      ];
                      const safeCategory = allowedCategories.includes(newBlog.category)
                        ? newBlog.category
                        : 'General';
                      formData.append('category', safeCategory);
                      formData.append('excerpt', newBlog.excerpt);
                      formData.append('content', newBlog.content);
                      if (newBlog.tags) {
                        formData.append('tags', newBlog.tags);
                      }
                      formData.append('published', 'true');
                      const res = await api.post('/blogs', formData, {
                        headers: { 'Content-Type': 'multipart/form-data' },
                      });
                      setBlogs((prev) => [...prev, res.data]);
                      setNewBlog({
                        title: '',
                        category: '',
                        excerpt: '',
                        content: '',
                        tags: '',
                      });
                    } catch (err) {
                      console.error(err);
                    }
                  }}
                >
                  <Typography variant="subtitle1" sx={{ width: '100%' }}>
                    Add Blog Post
                  </Typography>
                  <TextField
                    label="Title"
                    size="small"
                    value={newBlog.title}
                    onChange={(e) => setNewBlog((p) => ({ ...p, title: e.target.value }))}
                    required
                    sx={{ minWidth: 220 }}
                  />
                  <TextField
                    label="Category"
                    size="small"
                    select
                    value={newBlog.category}
                    onChange={(e) => setNewBlog((p) => ({ ...p, category: e.target.value }))}
                    sx={{ minWidth: 160 }}
                  >
                    <MenuItem value="Legal Updates">Legal Updates</MenuItem>
                    <MenuItem value="Case Studies">Case Studies</MenuItem>
                    <MenuItem value="Legal Tips">Legal Tips</MenuItem>
                    <MenuItem value="News">News</MenuItem>
                    <MenuItem value="General">General</MenuItem>
                  </TextField>
                  <TextField
                    label="Excerpt"
                    size="small"
                    sx={{ flex: 1, minWidth: 260 }}
                    value={newBlog.excerpt}
                    onChange={(e) => setNewBlog((p) => ({ ...p, excerpt: e.target.value }))}
                    required
                  />
                  <TextField
                    label="Tags (comma separated)"
                    size="small"
                    sx={{ minWidth: 220 }}
                    value={newBlog.tags}
                    onChange={(e) => setNewBlog((p) => ({ ...p, tags: e.target.value }))}
                  />
                  <TextField
                    label="Content (HTML or text)"
                    size="small"
                    multiline
                    minRows={2}
                    sx={{ width: '100%' }}
                    value={newBlog.content}
                    onChange={(e) => setNewBlog((p) => ({ ...p, content: e.target.value }))}
                    required
                  />
                  <Box sx={{ width: '100%', textAlign: 'right' }}>
                    <Button type="submit" variant="contained" size="small">
                      Publish
                    </Button>
                  </Box>
                </Box>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Title</TableCell>
                      <TableCell>Category</TableCell>
                      <TableCell>Published</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {blogs.map((b) => (
                      <TableRow key={b._id}>
                        <TableCell>{b.title}</TableCell>
                        <TableCell>{b.category}</TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={b.published ? 'Published' : 'Draft'}
                            color={b.published ? 'success' : 'default'}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <IconButton
                            size="small"
                            color="error"
                            title="Delete"
                            onClick={() => deleteBlog(b._id)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Paper>
            )}

            {activeTab === 5 && (
              <Paper sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Case Studies
                </Typography>
                <Box
                  component="form"
                  sx={{ mb: 3, display: 'flex', flexWrap: 'wrap', gap: 2 }}
                  onSubmit={async (e) => {
                    e.preventDefault();
                    try {
                      const formData = new FormData();
                      formData.append('title', newCaseStudy.title);
                      formData.append('category', newCaseStudy.category || 'Environmental');
                      formData.append('description', newCaseStudy.description);
                      formData.append('challenge', newCaseStudy.challenge);
                      formData.append('solution', newCaseStudy.solution);
                      formData.append('result', newCaseStudy.result);
                      formData.append('featured', 'false');
                      const res = await api.post('/cases', formData, {
                        headers: { 'Content-Type': 'multipart/form-data' },
                      });
                      setCases((prev) => [...prev, res.data]);
                      setNewCaseStudy({
                        title: '',
                        category: '',
                        description: '',
                        challenge: '',
                        solution: '',
                        result: '',
                      });
                    } catch (err) {
                      console.error(err);
                    }
                  }}
                >
                  <Typography variant="subtitle1" sx={{ width: '100%' }}>
                    Add Case Study
                  </Typography>
                  <TextField
                    label="Title"
                    size="small"
                    value={newCaseStudy.title}
                    onChange={(e) =>
                      setNewCaseStudy((p) => ({ ...p, title: e.target.value }))
                    }
                    required
                    sx={{ minWidth: 220 }}
                  />
                  <TextField
                    label="Category"
                    size="small"
                    value={newCaseStudy.category}
                    onChange={(e) =>
                      setNewCaseStudy((p) => ({ ...p, category: e.target.value }))
                    }
                    sx={{ minWidth: 160 }}
                  />
                  <TextField
                    label="Short Description"
                    size="small"
                    sx={{ flex: 1, minWidth: 260 }}
                    value={newCaseStudy.description}
                    onChange={(e) =>
                      setNewCaseStudy((p) => ({ ...p, description: e.target.value }))
                    }
                    required
                  />
                  <TextField
                    label="Challenge"
                    size="small"
                    sx={{ width: '100%' }}
                    multiline
                    minRows={2}
                    value={newCaseStudy.challenge}
                    onChange={(e) =>
                      setNewCaseStudy((p) => ({ ...p, challenge: e.target.value }))
                    }
                    required
                  />
                  <TextField
                    label="Solution"
                    size="small"
                    sx={{ width: '100%' }}
                    multiline
                    minRows={2}
                    value={newCaseStudy.solution}
                    onChange={(e) =>
                      setNewCaseStudy((p) => ({ ...p, solution: e.target.value }))
                    }
                    required
                  />
                  <TextField
                    label="Result"
                    size="small"
                    sx={{ width: '100%' }}
                    multiline
                    minRows={2}
                    value={newCaseStudy.result}
                    onChange={(e) =>
                      setNewCaseStudy((p) => ({ ...p, result: e.target.value }))
                    }
                    required
                  />
                  <Box sx={{ width: '100%', textAlign: 'right' }}>
                    <Button type="submit" variant="contained" size="small">
                      Add
                    </Button>
                  </Box>
                </Box>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Title</TableCell>
                      <TableCell>Category</TableCell>
                      <TableCell>Featured</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {cases.map((c) => (
                      <TableRow key={c._id}>
                        <TableCell>{c.title}</TableCell>
                        <TableCell>{c.category}</TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={c.featured ? 'Yes' : 'No'}
                            color={c.featured ? 'primary' : 'default'}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <IconButton
                            size="small"
                            color="error"
                            title="Delete"
                            onClick={() => deleteCase(c._id)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Paper>
            )}
          </Box>
        )}
      </Container>
    </Box>
  );
};

export default AdminDashboard;

